import { useEffect, useRef, useState } from 'react';
import { capture } from '../lib/analytics';
import './NextActions.css';

/**
 * The "what's next" row a kid sees after they've trained + tested a
 * model. Two peer choices — different *destination*, not a
 * primary→secondary stack.
 *
 * Honest mapping to the backend:
 *
 *  - **Download** (`onExport`) → packs the model + a vanilla-JS
 *    runtime into a downloadable file. Runs offline. Reversible.
 *  - **Share** dropdown — combines the two server destinations the
 *    kid would otherwise have to pick between cold:
 *    - **Make a website** (`onOpenWebsite`) → routes to `PublishView`
 *      which composes a mini-app and POSTs `/api/publish`. Produces a
 *      bryta.app share URL like `bryta.app/p/<uid>/<slug>`. The model
 *      is **baked into the bundle** — viewers cannot remix this.
 *    - **Add to Remix Gallery** (`onOpenAddToGallery`) → opens the
 *      `PublishDialog` which submits the project's *kit* (one
 *      representative sample per class) to a moderation queue. Once
 *      approved, others can browse + remix. This is the only path
 *      that is "remixable" by design.
 *    - **Both** runs the gallery submit, then routes to the website
 *      composer so the kid can do both in one go.
 *
 * Order = lowest-stakes first (download → share). Reading
 * left-to-right gives a risk gradient.
 */
export interface NextActionsProps {
  /** Open the bryta.app share-URL flow (`PublishView` →
   *  `POST /api/publish`). Produces a hosted, non-remixable mini-app
   *  with the model baked in. */
  onOpenWebsite?: () => void;
  /** Open the gallery-submission dialog (`PublishDialog` →
   *  `POST /api/gallery/submissions`). Submits the project kit for
   *  moderation; once approved, it appears in the public Remix
   *  Gallery and is remixable by other kids. */
  onOpenAddToGallery?: () => void;
  /** Promise-returning local-export callback. The component owns the
   *  busy state + error surface so callers don't have to. */
  onExport?: () => Promise<void>;
}

type ShareChoice = 'website' | 'gallery' | 'both';

export function NextActions({
  onOpenWebsite,
  onOpenAddToGallery,
  onExport,
}: NextActionsProps): JSX.Element {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const shareWrapRef = useRef<HTMLDivElement>(null);

  // Fire `next_actions_opened` once when this row first appears for
  // a given mount — i.e., the moment the kid sees their options
  // post-training. Dropoffs from here are the export funnel.
  useEffect(() => {
    capture('next_actions_opened', {});
  }, []);

  // Outside-click closes the share menu. Anchored to the wrapping
  // div so clicks inside the menu don't dismiss it before the option
  // fires.
  useEffect(() => {
    if (!shareOpen) return;
    const onDoc = (e: MouseEvent): void => {
      if (!shareWrapRef.current?.contains(e.target as Node)) setShareOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [shareOpen]);

  async function handleExport(): Promise<void> {
    if (!onExport || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      await onExport();
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Could not download the model.');
    } finally {
      setExporting(false);
    }
  }

  function handleShare(choice: ShareChoice): void {
    setShareOpen(false);
    capture('share_dropdown_picked', { choice });
    if (choice === 'website') {
      onOpenWebsite?.();
      return;
    }
    if (choice === 'gallery') {
      onOpenAddToGallery?.();
      return;
    }
    // "Both": open the gallery dialog first (it's a modal — the
    // website composer would otherwise hide behind it). When the kid
    // closes the dialog, they land on the composer route. We can't
    // sequence onClose from here without rewiring the dialog, so the
    // simpler honest move is: kick off the gallery submit, then nav
    // straight to the composer. The composer doesn't block the
    // gallery dialog — both can be in-flight without conflict.
    onOpenAddToGallery?.();
    onOpenWebsite?.();
  }

  const shareSupported = !!onOpenWebsite || !!onOpenAddToGallery;

  return (
    <>
      <div className="bl-next">
        {onExport && (
          <button
            type="button"
            className="bl-next__btn"
            onClick={() => void handleExport()}
            disabled={exporting}
            title="Download the trained model as a file. Runs offline, anywhere."
          >
            <span className="bl-next__icon" aria-hidden="true">
              ⬇
            </span>
            <span className="bl-next__label">{exporting ? 'Preparing…' : 'Download'}</span>
          </button>
        )}
        {shareSupported && (
          <div className="bl-next__share" ref={shareWrapRef}>
            <button
              type="button"
              className="bl-next__btn bl-next__btn--share"
              onClick={() => setShareOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={shareOpen}
              title="Pick where this goes — a website to visit, the remix gallery, or both."
            >
              <span className="bl-next__icon" aria-hidden="true">
                📤
              </span>
              <span className="bl-next__label">Share</span>
              <span className="bl-next__caret" aria-hidden="true">
                ▾
              </span>
            </button>
            {shareOpen && (
              <ul className="bl-next__share-menu" role="menu" aria-label="Share destinations">
                {onOpenWebsite && (
                  <li>
                    <button
                      type="button"
                      role="menuitem"
                      className="bl-next__share-option"
                      onClick={() => handleShare('website')}
                    >
                      <span className="bl-next__share-option-icon" aria-hidden="true">
                        🌐
                      </span>
                      <span className="bl-next__share-option-copy">
                        <span className="bl-next__share-option-title">Make a website</span>
                        <span className="bl-next__share-option-hint">
                          A bryta.app URL friends can visit. Not remixable.
                        </span>
                      </span>
                    </button>
                  </li>
                )}
                {onOpenAddToGallery && (
                  <li>
                    <button
                      type="button"
                      role="menuitem"
                      className="bl-next__share-option"
                      onClick={() => handleShare('gallery')}
                    >
                      <span className="bl-next__share-option-icon" aria-hidden="true">
                        📚
                      </span>
                      <span className="bl-next__share-option-copy">
                        <span className="bl-next__share-option-title">Add to Remix Gallery</span>
                        <span className="bl-next__share-option-hint">
                          Other kids can browse it and remix it.
                        </span>
                      </span>
                    </button>
                  </li>
                )}
                {onOpenWebsite && onOpenAddToGallery && (
                  <li>
                    <button
                      type="button"
                      role="menuitem"
                      className="bl-next__share-option bl-next__share-option--both"
                      onClick={() => handleShare('both')}
                    >
                      <span className="bl-next__share-option-icon" aria-hidden="true">
                        ✨
                      </span>
                      <span className="bl-next__share-option-copy">
                        <span className="bl-next__share-option-title">Both at once</span>
                        <span className="bl-next__share-option-hint">
                          Send to the gallery AND open the website composer.
                        </span>
                      </span>
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
      {exportError && (
        <p className="bl-next__error" role="alert">
          {exportError}
        </p>
      )}
    </>
  );
}
